import { FC } from 'react'

import 'primeicons/primeicons.css'
import 'primereact/resources/primereact.css'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { InputText } from 'primereact/inputtext'
import { classNames } from 'primereact/utils'
import { Button } from 'primereact/button'
import { useTriggerMAVProcess } from '../api/misc-API'
import toast from 'react-hot-toast'
import { useRecoilValue } from 'recoil'
import { LoadingIcon } from '../components/Icons'
import { Link } from 'react-router-dom'
import { currentUserState } from '../utils/state-atoms'
import { canMutate } from '../utils/roles'

type ExternalCaseParams = {
  runId: string
  caseNumber: string
}

const ExternalCase: FC = () => {
  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<ExternalCaseParams>()

  const [{ loading }, triggerExternalCase] = useTriggerMAVProcess()
  const currentUser = useRecoilValue(currentUserState)
  const userCanMutate = canMutate(currentUser?.role?.roleId)

  const getFormErrorMessage = name => {
    return errors[name] ? (
      <small className="error">{errors[name].message}</small>
    ) : (
      <small className="error">&nbsp;</small>
    )
  }

  const onSubmit: SubmitHandler<ExternalCaseParams> = async (formData: ExternalCaseParams) => {
    toast.dismiss()
    const { runId, caseNumber } = formData

    try {
      await triggerExternalCase({ runId, caseNumber: caseNumber } as any)
      toast.success(
        <span>
          Run{' '}
          <Link
            className="underline text-gm-blue"
            to={`/submissions/${runId}`}
            onClick={() => {
              toast.dismiss()
            }}>
            {runId}
          </Link>{' '}
          and case {caseNumber} submitted.
        </span>,
        { duration: 15 * 1000 }
      )
    } catch (error) {
      console.error('Error triggering external case', error)
      toast.error('Error triggering external case')
    }
  }

  return (
    <div
      className="basis-0 grow flex flex-col items-stretch mt-6 min-w-full"
      onClick={() => toast.dismiss()}>
      <div className="flex items-center justify-between pb-4">
        <h1 className="page-title">External Case</h1>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="w-full grid grid-cols-2 gap-x-10 gap-y-4 mt-8">
        <div className="flex flex-col items-stretch">
          <Controller
            name="runId"
            control={control}
            rules={{ required: 'Run ID is required.' }}
            render={({ field, fieldState }) => (
              <>
                <label htmlFor={field.name} className="mb-2">
                  Run ID <span className="text-red-500">*</span>
                </label>
                <InputText
                  id={field.name}
                  value={field.value}
                  placeholder="[vehicle id]_[project id]_[run id], eg 26_FW024_R100"
                  className={classNames({ error: fieldState.error })}
                  onChange={e => field.onChange(e.target.value)}
                />
                {getFormErrorMessage(field.name)}
              </>
            )}
          />
        </div>
        <div className="flex flex-col items-stretch">
          <Controller
            name="caseNumber"
            control={control}
            rules={{ required: 'Case number is required.' }}
            render={({ field, fieldState }) => (
              <>
                <label htmlFor={field.name} className="mb-2">
                  Case Number <span className="text-red-500">*</span>
                </label>
                <InputText
                  id={field.name}
                  {...field}
                  className={classNames({ error: fieldState.error })}
                />
                {getFormErrorMessage(field.name)}
              </>
            )}
          />
        </div>
        <div className="col-span-2 text-gray-400 italic">
          Note: This process assumes the dependency files already exist for this case. The process
          will build a submission with a file trigger workflow.
        </div>
        <div className="flex items-center justify-end space-x-4 col-span-2">
          <Button
            type="submit"
            disabled={loading || !userCanMutate}
            className="flex items-center justify-center">
            {loading && <LoadingIcon className="fill-white w-4 h-4 animate-spin mr-1" />}
            <span>Submit</span>
          </Button>
        </div>
      </form>
    </div>
  )
}

export default ExternalCase
